import { apiClient } from './client'
import {
  WAREHOUSE_ENDPOINTS,
  appendWarehouseQueryParam,
  getCurrentWarehouse,
  resolveWarehouseApiUrl,
  type WarehouseEndpoint,
} from '@/features/warehouse/config'

const CATALOG_PREFIX = '/api/public/catalog'
const DEFAULT_CATALOG_PAGE_SIZE = 15

export type ProductStatus = 'in_stock' | 'out_of_stock' | 'in_progress'
export type ProductBadge = 'new' | 'hit' | 'best_offer'

export type ImageSizes = {
  thumb?: string
  medium?: string
  original?: string
}

export type Product = {
  id: string
  slug?: string
  warehouseId?: string
  warehouseName?: string
  warehouseScopedId?: string
  title: string
  brand?: string
  price: number
  finalPrice?: number | null
  originalPrice?: number | null
  discountPercent?: number | null
  status: ProductStatus
  variantId?: number
  available_quantity?: number
  /** Несколько вариантов для выбора в карточке (каталог / lite-лист) */
  variants?: ProductVariant[]
  arrivalDate?: string
  reviewsCount?: number
  rating?: number
  badges?: ProductBadge[]
  promoBadgeText?: string | null
  promoExpiresAt?: string | null
  images?: Array<string | ImageSizes>
  characteristics?: ProductCharacteristic[]
  overview_videos?: OverviewVideo[]
  overview_video?: OverviewVideo | null
}

export type ProductVariant = {
  id: number
  is_subscribed: boolean
  sku: string
  title: string
  price: number
  final_price?: number | null
  discount_amount?: number | null
  discount_percent?: number | null
  currency?: string
  position: number | null
  images: Array<string | ImageSizes>
  available_quantity?: number
  quality?: string
  manufacturer?: string
  promo_expires_at?: string | null
}

export type ProductCharacteristic = {
  name: string
  value: string
}

export type OverviewVideoSourceType =
  | 'none'
  | 'external_url'
  | 'vk_video'
  | 'uploaded_file'

export type OverviewVideoStatus =
  | 'idle'
  | 'pending'
  | 'processing'
  | 'ready'
  | 'failed'

export type OverviewVideoFileSource = {
  label: string
  url: string
  mime_type?: string | null
  height?: number | null
  width?: number | null
}

export type OverviewVideo = {
  id?: number | null
  review_slug?: string | null
  detail_path?: string | null
  created_at?: string | null
  source_type: OverviewVideoSourceType
  player_type: 'html5' | 'iframe'
  status: OverviewVideoStatus
  title?: string | null
  description?: string | null
  preview_image?: string | null
  preview_focus_x?: number
  preview_focus_y?: number
  error?: string | null
  url?: string | null
  embed_url?: string | null
  duration_seconds?: number | null
  width?: number | null
  height?: number | null
  sources: OverviewVideoFileSource[]
}

export type BreadcrumbItem = { title: string; path: string }
export type CategoryBreadcrumbItem = { title: string; slug: string }

export type Review = {
  id: number
  full_name: string
  rating: number
  text: string
  created_at: string
  is_edited?: boolean
  edited_at?: string | null
  replies?: ReviewReply[]
}

export type CreateReviewRequest = {
  product_id: number
  rating: number
  text: string
  review_publication_consent: boolean
}

export type ReviewReply = {
  id: number
  display_name: string
  text: string
  created_at: string
  is_edited?: boolean
  edited_at?: string | null
}

export type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export type ProductDetail = {
  id: number
  name: string
  slug: string
  warehouseId?: string
  warehouseName?: string
  warehouseScopedId?: string
  description: string
  status?: string
  is_active?: boolean
  is_best_offer?: boolean
  is_new?: boolean
  is_hit?: boolean
  created_at?: string
  badges?: ProductBadge[]
  promo_badge_text?: string | null
  promo_expires_at?: string | null
  variants: ProductVariant[]
  characteristics?: ProductCharacteristic[]
  breadcrumb?: BreadcrumbItem[]
  overview_videos?: OverviewVideo[]
  overview_video?: OverviewVideo | null
  recommended?: ProductRecommendedRaw[]
  avg_rating?: number | null
  reviews_count?: number | null
}

export type ProductTrackingPayload = {
  source?: string
  source_name?: string
  share_method?: 'copy_link' | 'open_share' | 'native_share' | null
  page_url?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
}

export type ProductRecommendedRaw = {
  id: number
  name: string
  slug: string
  warehouseId?: string
  warehouseName?: string
  warehouseScopedId?: string
  description: string
  is_best_offer?: boolean
  is_new?: boolean
  is_hit?: boolean
  badges?: ProductBadge[]
  promo_badge_text?: string | null
  promo_expires_at?: string | null
  variants: ProductVariant[]
  characteristics?: ProductCharacteristic[]
  breadcrumb?: BreadcrumbItem[]
  avg_rating?: number
  reviews_count?: number
}

export type CategoryTreeNode = {
  id: number
  slug: string
  title: string
  productCount: number
  children: CategoryTreeNode[]
}

export type Brand = {
  id: number | string
  title: string
  logo?: string
  slug?: string
  productsCount?: number
}

export type Arrival = {
  id: string
  slug?: string
  url?: string
  date: string
  dateLabel?: string
  createdAt?: string | null
  image?: string
  productsCount?: number
  source?: string
  moyskladName?: string
  moyskladStateName?: string
  moyskladApplicable?: boolean | null
  moyskladStoreName?: string
}

export type ArrivalDetail = Arrival & {
  products: Product[]
  productsPage?: number
  productsPageSize?: number
  productsTotal?: number
  productsHasMore?: boolean
}

export type HomeSectionCategory = { id: number; title: string; slug: string }

export type HomeSection = {
  id: number
  category?: HomeSectionCategory | null
  subcategory?: HomeSectionCategory | null
  catalogUrl: string
  products: Product[]
}

export type HomePromoSection = {
  catalogUrl?: string
  createdAt?: string | null
  products: Product[]
}

export type VideoReview = {
  id: number
  slug: string
  createdAt?: string | null
  title: string
  description: string
  previewFocusX: number
  previewFocusY: number
  productsCount: number
  productTitles: string[]
  detailPath: string
  video: OverviewVideo | null
  products?: Product[]
}

export type HomeVideoReviewSection = {
  catalogUrl?: string
  reviews: VideoReview[]
}

export type ProductShowcaseListItem = {
  title: string
  slug: string
  publicationDate: string
  expiresAt?: string | null
  itemsCount: number
}

export type ProductShowcaseProduct = {
  id: string
  variantId: number
  sku: string
  name: string
  slug: string
  image?: string | null
  basePrice: number
  publicPrice: number
  price: number
  badges?: ProductBadge[]
  promoBadgeText?: string | null
  promoExpiresAt?: string | null
  discountPercent?: number | null
}

export type ProductShowcaseDetail = {
  title: string
  slug: string
  publicationDate: string
  expiresAt?: string | null
  products: ProductShowcaseProduct[]
  productsTotal?: number
  productsHasMore?: boolean
  productsPage?: number
  productsPageSize?: number
}

export type HomePageData = {
  bestOffers: Product[]
  hits: Product[]
  discounts: HomePromoSection | null
  videoReviews: HomeVideoReviewSection | null
  brands: Brand[]
  arrivals: Arrival[]
  sections: HomeSection[]
  youViewed: Product[]
}

const HOME_URL = '/api/public/catalog/home/'
const HOME_BANNER_URL = '/api/public/catalog/home/banner/'

type BrandRaw = {
  id: number | string
  title: string
  logo?: string | null
  slug?: string | null
  products_count?: number | string | null
}

/** Ответ бэка по продукту с главной (snake_case) */
type HomeProductRaw = {
  id: string
  slug?: string
  title: string
  price: number | string
  final_price?: number | string | null
  discount_amount?: number | string | null
  discount_percent?: number | string | null
  status: ProductStatus
  variantId?: number
  available_quantity?: number
  arrivalDate?: string
  reviewsCount?: number | null
  rating?: number | null
  reviews_count?: number | null
  avg_rating?: number | null
  badges?: ProductBadge[]
  promo_badge_text?: string | null
  promo_expires_at?: string | null
  images?: Array<string | ImageSizes>
  characteristics?: ProductCharacteristic[]
  overview_videos?: OverviewVideo[]
  overview_video?: OverviewVideo | null
}

type VideoReviewRaw = {
  id: number | string
  slug: string
  created_at?: string | null
  overview_video_title?: string | null
  overview_video_description?: string | null
  preview_focus_x?: number | null
  preview_focus_y?: number | null
  products_count?: number | string | null
  product_titles?: string[]
  detail_path: string
  video?: OverviewVideo | null
  products?: HomeProductRaw[]
}

function normalizeHomeProduct(r: HomeProductRaw): Product {
  const base = Number(r.price)
  const finalVal = r.final_price != null ? Number(r.final_price) : null
  const finalPrice = finalVal ?? base
  const explicitDiscountPercent = r.discount_percent != null ? Number(r.discount_percent) : null
  const hasPriceDiscount = base > 0 && finalPrice > 0 && base > finalPrice
  const discountPercent =
    explicitDiscountPercent != null && explicitDiscountPercent > 0
      ? explicitDiscountPercent
      : hasPriceDiscount
        ? ((base - finalPrice) / base) * 100
        : null
  const hasDiscount =
    (discountPercent != null && discountPercent > 0) ||
    Number(r.discount_amount ?? 0) > 0 ||
    hasPriceDiscount
  const reviewsCountRaw = r.reviewsCount ?? r.reviews_count
  const ratingRaw = r.rating ?? r.avg_rating
  return {
    id: r.id,
    slug: r.slug,
    title: r.title,
    status: r.status,
    variantId: r.variantId,
    available_quantity: r.available_quantity != null ? Number(r.available_quantity) : undefined,
    arrivalDate: r.arrivalDate,
    reviewsCount: reviewsCountRaw != null ? Number(reviewsCountRaw) : 0,
    rating: ratingRaw != null ? Number(ratingRaw) : 0,
    badges: r.badges,
    promoBadgeText: r.promo_badge_text ?? null,
    promoExpiresAt: r.promo_expires_at ?? null,
    images: r.images,
    characteristics: r.characteristics,
    overview_videos: Array.isArray(r.overview_videos) ? r.overview_videos : undefined,
    overview_video: r.overview_video ?? null,
    price: finalPrice,
    finalPrice,
    originalPrice: hasDiscount ? base : undefined,
    discountPercent: discountPercent ?? undefined,
  }
}

function normalizeVideoReview(raw: VideoReviewRaw): VideoReview {
  return {
    id: Number(raw.id),
    slug: raw.slug,
    createdAt: raw.created_at ?? raw.video?.created_at ?? null,
    title: raw.overview_video_title?.trim() || 'Видеообзор',
    description: raw.overview_video_description?.trim() || '',
    previewFocusX: typeof raw.preview_focus_x === 'number' ? raw.preview_focus_x : 50,
    previewFocusY: typeof raw.preview_focus_y === 'number' ? raw.preview_focus_y : 50,
    productsCount:
      raw.products_count != null
        ? Number(raw.products_count) || 0
        : Array.isArray(raw.products)
          ? raw.products.length
          : 0,
    productTitles: Array.isArray(raw.product_titles) ? raw.product_titles : [],
    detailPath: raw.detail_path,
    video: raw.video ?? null,
    products: Array.isArray(raw.products) ? raw.products.map(normalizeHomeProduct) : undefined,
  }
}

function normalizeShowcaseListItem(
  raw: ProductShowcaseListItemRaw
): ProductShowcaseListItem {
  return {
    title: raw.title,
    slug: raw.slug,
    publicationDate: raw.publication_date,
    expiresAt: raw.expires_at ?? null,
    itemsCount: Number(raw.items_count) || 0,
  }
}

function normalizeShowcaseProduct(
  raw: ProductShowcaseProductRaw
): ProductShowcaseProduct {
  return {
    id: String(raw.id),
    variantId: Number(raw.variant_id),
    sku: raw.sku,
    name: raw.name,
    slug: raw.slug,
    image: raw.image ?? null,
    basePrice: Number(raw.base_price) || 0,
    publicPrice: Number(raw.public_price) || 0,
    price: Number(raw.price) || 0,
    badges: raw.badges ?? [],
    promoBadgeText: raw.promo_badge_text ?? null,
    promoExpiresAt: raw.promo_expires_at ?? null,
    discountPercent: raw.discount_percent ?? null,
  }
}

function normalizeShowcaseDetail(
  raw: ProductShowcaseDetailRaw
): ProductShowcaseDetail {
  const productsPage = Array.isArray(raw.products)
    ? {
        count: raw.products.length,
        next: null,
        previous: null,
        results: raw.products,
      }
    : raw.products

  return {
    title: raw.title,
    slug: raw.slug,
    publicationDate: raw.publication_date,
    expiresAt: raw.expires_at ?? null,
    products: productsPage.results.map(normalizeShowcaseProduct),
    productsTotal: Number(productsPage.count) || 0,
    productsHasMore: Boolean(productsPage.next),
    productsPage: raw.products_page,
    productsPageSize: raw.products_page_size,
  }
}

type RawHomePageData = Omit<
  HomePageData,
  'bestOffers' | 'hits' | 'discounts' | 'videoReviews' | 'youViewed' | 'sections' | 'brands'
> & {
  bestOffers: HomeProductRaw[]
  hits: HomeProductRaw[]
  brands: BrandRaw[]
  discounts?: { catalogUrl: string; createdAt?: string | null; products: HomeProductRaw[] } | null
  videoReviews?: { catalogUrl?: string; reviews: VideoReviewRaw[] } | null
  youViewed: HomeProductRaw[]
  sections: (Omit<HomeSection, 'products'> & { products: HomeProductRaw[] })[]
}

type VideoReviewListResponseRaw = PaginatedResponse<VideoReviewRaw>

type ProductShowcaseListItemRaw = {
  title: string
  slug: string
  publication_date: string
  expires_at?: string | null
  items_count: number | string
}

type ProductShowcaseProductRaw = {
  id: number | string
  variant_id: number | string
  sku: string
  name: string
  slug: string
  image?: string | null
  base_price: number | string
  public_price: number | string
  price: number | string
  badges?: ProductBadge[]
  promo_badge_text?: string | null
  promo_expires_at?: string | null
  discount_percent?: number | null
}

type ProductShowcaseDetailRaw = {
  title: string
  slug: string
  publication_date: string
  expires_at?: string | null
  products: ProductShowcaseProductRaw[] | PaginatedResponse<ProductShowcaseProductRaw>
  products_page?: number
  products_page_size?: number
}

export type HomeBanner = {
  id: number
  title: string
  description: string
  banner_type?: 'default' | 'contacts'
  image: string | ImageSizes | null
  image_focus_x?: number | null
  image_focus_y?: number | null
  image_zoom_percent?: number | null
  contacts?: StoreSupportContact[]
}

export type StoreSupportContactType =
  | 'telegram'
  | 'whatsapp'
  | 'phone'
  | 'email'
  | 'instagram'
  | 'other'

export type StoreSupportContact = {
  name: string
  label: string | null
  contact_type: StoreSupportContactType
  value: string
  url: string | null
}

export type ContactStore = {
  id: number
  title: string
  order: number | null
  description: string
  latitude: number | null
  longitude: number | null
  directions: string
  osm_url?: string | null
  phones: string[]
  addresses: string[]
  socials?: StoreSocialLink[]
  support_contacts?: StoreSupportContact[]
  opening_hours?: StoreOpeningHour[]
}

export type StoreOpeningHour = {
  weekday: number
  open_time: string | null
  close_time: string | null
  is_closed: boolean
}

export type PickupStore = ContactStore & {
  pickup_available: boolean
  available_items: Array<{
    variant_id: number
    requested: number
    available: number
  }>
  warehouse_ids: number[]
}

export type SocialLink = {
  id: number
  title: string
  icon: string
  link: string
}

export type StoreSocialLink = {
  title: string
  icon: string | null
  link: string
}

export type ContactsResponse = {
  stores: ContactStore[]
  socials: SocialLink[]
}

type SupportContactRaw = {
  name: string
  label?: string | null
  contact_type: string
  value: string
  url?: string | null
}

type ContactStoreRaw = Omit<ContactStore, 'latitude' | 'longitude' | 'support_contacts'> & {
  latitude: number | string | null
  longitude: number | string | null
  support_contacts?: SupportContactRaw[]
}

type ContactsResponseRaw = {
  stores: ContactStoreRaw[]
  socials: SocialLink[]
}

const toNullableNumber = (value: number | string | null): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : null
}

const SUPPORT_CONTACT_TYPES = new Set<string>([
  'telegram',
  'whatsapp',
  'phone',
  'email',
  'instagram',
  'other',
])

const normalizeSupportContactType = (t: string): StoreSupportContactType =>
  SUPPORT_CONTACT_TYPES.has(t) ? (t as StoreSupportContactType) : 'other'

function normalizeBrand(raw: BrandRaw): Brand {
  return {
    id: raw.id,
    title: raw.title,
    logo: raw.logo ?? undefined,
    slug: raw.slug ?? undefined,
    productsCount: raw.products_count != null ? Number(raw.products_count) || 0 : undefined,
  }
}

const normalizeContactStore = (store: ContactStoreRaw): ContactStore => ({
  ...store,
  latitude: toNullableNumber(store.latitude),
  longitude: toNullableNumber(store.longitude),
  support_contacts: store.support_contacts?.map((c) => ({
    name: c.name,
    label: c.label ?? null,
    contact_type: normalizeSupportContactType(c.contact_type),
    value: c.value,
    url: c.url ?? null,
  })),
})

export const homeApi = {
  getHomePage: () =>
    apiClient
      .get<RawHomePageData>(HOME_URL)
      .then((r) => r.data)
      .then((data) => ({
        bestOffers: data.bestOffers.map(normalizeHomeProduct),
        hits: data.hits.map(normalizeHomeProduct),
        discounts: data.discounts
          ? {
              ...data.discounts,
              products: data.discounts.products.map(normalizeHomeProduct),
            }
          : null,
        videoReviews: data.videoReviews
          ? {
              ...data.videoReviews,
              reviews: data.videoReviews.reviews.map(normalizeVideoReview),
            }
          : null,
        brands: data.brands.map(normalizeBrand),
        arrivals: data.arrivals,
        sections: data.sections.map((s) => ({
          ...s,
          products: s.products.map(normalizeHomeProduct),
        })),
        youViewed: data.youViewed.map(normalizeHomeProduct),
      })),
  getHomeBanner: () =>
    apiClient.get<HomeBanner[]>(HOME_BANNER_URL).then((r) => r.data),
}

export const videoReviewsApi = {
  list: (params?: { page?: number; q?: string }) =>
    apiClient
      .get<VideoReviewListResponseRaw>(`${CATALOG_PREFIX}/video-reviews/`, {
        params: {
          page: params?.page,
          q: params?.q,
        },
      })
      .then((r) => ({
        ...r.data,
        results: r.data.results.map(normalizeVideoReview),
      })),
  retrieve: (slug: string) =>
    apiClient
      .get<VideoReviewRaw>(`${CATALOG_PREFIX}/video-reviews/${slug}/`)
      .then((r) => normalizeVideoReview(r.data)),
}

const CONTACTS_URL = '/api/public/contacts/'
const STORES_URL = '/api/public/contacts/stores/'
const PICKUP_STORES_URL = '/api/public/contacts/pickup-stores/'
const CDEK_PICKUP_POINTS_URL = '/api/public/contacts/cdek/pickup-points/'
const CDEK_PICKUP_ESTIMATE_URL = '/api/public/contacts/cdek/pickup-estimate/'
const LEGAL_URL = '/api/public/legal/'

export const contactsApi = {
  getContacts: () =>
    apiClient
      .get<ContactsResponseRaw>(CONTACTS_URL)
      .then((r) => r.data)
      .then((data) => ({
        ...data,
        stores: data.stores.map(normalizeContactStore),
      })),
  getStores: () =>
    apiClient
      .get<ContactStoreRaw[]>(STORES_URL)
      .then((r) => r.data)
      .then((stores) => stores.map(normalizeContactStore)),
  getStore: (id: number | string) =>
    apiClient
      .get<ContactStoreRaw>(`${STORES_URL}${id}/`)
      .then((r) => normalizeContactStore(r.data)),
  getPickupStores: (items: Array<{ variant_id: number; quantity: number }>) =>
    apiClient
      .get<PickupStore[]>(PICKUP_STORES_URL, {
        params: {
          items: items.map((item) => `${item.variant_id}:${item.quantity}`).join(','),
        },
      })
      .then((r) => r.data.map((store) => ({
        ...normalizeContactStore(store),
        pickup_available: store.pickup_available,
        available_items: store.available_items,
        warehouse_ids: store.warehouse_ids,
      }))),
  getCdekPickupPoints: (city: string) =>
    apiClient
      .get<{ city: string; points: CdekPickupPoint[] }>(CDEK_PICKUP_POINTS_URL, { params: { city } })
      .then((r) => r.data),
  getCdekPickupEstimate: (city: string, pointCode: string) =>
    apiClient
      .get<{ cost: number | string | null; days: string | null }>(CDEK_PICKUP_ESTIMATE_URL, { params: { city, point_code: pointCode } })
      .then((r) => r.data),
}

export type CdekPickupPoint = {
  code: string
  uuid?: string | null
  name: string
  city: string
  address: string
  work_time: string
  latitude: number | string | null
  longitude: number | string | null
  postal_code?: string | null
  delivery_cost?: number | string | null
  delivery_days?: string | null
}

export type LegalDocument = {
  slug: 'rules' | 'privacy' | 'cookies' | string
  title: string
  revision_date: string
  body: string
  meta_description: string
  updated_at: string
}

export const legalApi = {
  list: () =>
    apiClient.get<LegalDocument[]>(LEGAL_URL).then((r) => r.data),
  retrieve: (slug: string) =>
    apiClient
      .get<LegalDocument>(`${LEGAL_URL}${encodeURIComponent(slug)}/`)
      .then((r) => r.data),
  acceptConsent: (payload: { document_slug: string; action: string }) =>
    apiClient.post(`${LEGAL_URL}consents/`, payload).then((r) => r.data),
}

export type CatalogProductListParams = {
  category?: string
  brand?: string
  page?: number
  page_size?: number
  type?: 'hits' | 'newest' | 'promotions'
  q?: string
  isBestOffer?: boolean
  isHit?: boolean
  hasDiscount?: boolean
  hasGift?: boolean
  hasPromotion?: boolean
}

export type CatalogProductListResponse = {
  count: number
  next: string | null
  previous: string | null
  results: ProductDetail[]
}

export type ArrivalsPage = PaginatedResponse<Arrival>

export type SearchSuggestion = {
  id: number
  name: string
  slug: string
  warehouseId: string
  warehouseName: string
  warehouseScopedId: string
  productPath: string
}

const attachWarehouseMetaToProductDetail = (
  product: ProductDetail,
  warehouse: WarehouseEndpoint
): ProductDetail => ({
  ...product,
  warehouseId: warehouse.id,
  warehouseName: warehouse.name,
  warehouseScopedId: `${warehouse.id}:${product.id}`,
})

const getSearchWarehouseOrder = (): WarehouseEndpoint[] => {
  const currentWarehouse = getCurrentWarehouse()
  const rest = WAREHOUSE_ENDPOINTS.filter((warehouse) => warehouse.id !== currentWarehouse.id)
  return [currentWarehouse, ...rest]
}

const isGlobalWarehouseSearchParams = (params?: CatalogProductListParams): boolean =>
  Boolean(
      params?.q &&
      !params?.category &&
      !params?.brand &&
      !params?.hasDiscount &&
      !params?.hasGift &&
      !params?.hasPromotion &&
      !params?.isBestOffer &&
      !params?.isHit &&
      !params?.type
  )

const getWarehouseProducts = async (
  warehouse: WarehouseEndpoint,
  params?: CatalogProductListParams
): Promise<CatalogProductListResponse> => {
  const url = resolveWarehouseApiUrl(warehouse, `${CATALOG_PREFIX}/products/`)

  const response = await apiClient.get<CatalogProductListResponse>(url, {
    params: {
      category: params?.category,
      brand: params?.brand,
      page: params?.page,
      page_size: params?.page_size,
      type: params?.type,
      q: params?.q,
      isBestOffer: params?.isBestOffer,
      isHit: params?.isHit,
      hasDiscount: params?.hasDiscount,
      hasGift: params?.hasGift,
      hasPromotion: params?.hasPromotion,
    },
  })

  return {
    ...response.data,
    results: response.data.results.map((product) =>
      attachWarehouseMetaToProductDetail(product, warehouse)
    ),
  }
}

const getProductsAcrossWarehouses = async (
  params?: CatalogProductListParams
): Promise<CatalogProductListResponse> => {
  const page = Math.max(1, params?.page ?? 1)
  const pageSize = Math.max(1, params?.page_size ?? DEFAULT_CATALOG_PAGE_SIZE)
  const perWarehouseLimit = page * pageSize
  const orderedWarehouses = getSearchWarehouseOrder()

  const results = await Promise.allSettled(
    orderedWarehouses.map((warehouse) =>
      getWarehouseProducts(warehouse, {
        ...params,
        page: 1,
        page_size: perWarehouseLimit,
      })
    )
  )

  const successful = results
    .map((result, index) => ({ result, warehouse: orderedWarehouses[index] }))
    .filter(
      (
        item
      ): item is {
        result: PromiseFulfilledResult<CatalogProductListResponse>
        warehouse: WarehouseEndpoint
      } => item.result.status === 'fulfilled'
    )

  const merged = successful.flatMap((item) => item.result.value.results)
  const count = successful.reduce((sum, item) => sum + item.result.value.count, 0)
  const start = (page - 1) * pageSize
  const end = start + pageSize
  const sliced = merged.slice(start, end)
  const hasNext = end < count

  return {
    count,
    next: hasNext ? String(page + 1) : null,
    previous: page > 1 ? String(page - 1) : null,
    results: sliced,
  }
}

export const getProductPathForWarehouse = (
  slugOrId: string | number,
  warehouseId?: string | null
): string => appendWarehouseQueryParam(`/products/${slugOrId}`, warehouseId)

export const searchApi = {
  getSuggestions: async (query: string, limit = 5): Promise<SearchSuggestion[]> => {
    const response = await getProductsAcrossWarehouses({
      q: query,
      page: 1,
      page_size: limit,
    })

    return response.results.map((product) => ({
      id: product.id,
      name: product.name,
      slug: product.slug,
      warehouseId: product.warehouseId ?? 'default',
      warehouseName: product.warehouseName ?? 'Склад',
      warehouseScopedId:
        product.warehouseScopedId ?? `${product.warehouseId ?? 'default'}:${product.id}`,
      productPath: getProductPathForWarehouse(product.slug || product.id, product.warehouseId),
    }))
  },
}

export type CompareCharacteristic = {
  name: string
  value: string
}

export type CompareVariantResult = {
  id: number
  sku: string
  product_name: string
  product?: number
  image: string | null
  price: {
    base: number
    final: number
    discount: number
  }
  characteristics: CompareCharacteristic[]
}

export const compareApi = {
  getVariants: (variantIds: number[]) =>
    apiClient
      .get<CompareVariantResult[]>(`${CATALOG_PREFIX}/compare/`, {
        params: { variants: variantIds.join(',') },
      })
      .then((r) => r.data),
}

export const catalogApi = {
  /**
   * Detal page request.
   * IMPORTANT: DRF `retrieve()` tracks recently viewed products on the backend.
   */
  retrieveProduct: (slugOrId: string, params?: Record<string, string>) =>
    apiClient
      .get<ProductDetail>(`${CATALOG_PREFIX}/products/${slugOrId}/`, { params })
      .then((r) => r.data),

  // Backward-compatible alias
  getProduct: (slugOrId: string, params?: Record<string, string>) =>
    apiClient.get<ProductDetail>(`${CATALOG_PREFIX}/products/${slugOrId}/`, { params }).then((r) => r.data),

  getCategoriesTree: () =>
    apiClient
      .get<CategoryTreeNode[]>(`${CATALOG_PREFIX}/categories/`)
      .then((r) => r.data),

  getCategoryBreadcrumb: (slug: string) =>
    apiClient
      .get<CategoryBreadcrumbItem[]>(`${CATALOG_PREFIX}/categories/${slug}/breadcrumb/`)
      .then((r) => r.data),

  getBrands: () =>
    apiClient
      .get<BrandRaw[]>(`${CATALOG_PREFIX}/brands/`)
      .then((r) => r.data.map(normalizeBrand)),

  getBrand: (slug: string) =>
    apiClient
      .get<BrandRaw>(`${CATALOG_PREFIX}/brands/${slug}/`)
      .then((r) => normalizeBrand(r.data)),

  getProducts: (params?: CatalogProductListParams) =>
    isGlobalWarehouseSearchParams(params)
      ? getProductsAcrossWarehouses(params)
      : apiClient
          .get<CatalogProductListResponse>(`${CATALOG_PREFIX}/products/`, {
            params: {
              category: params?.category,
              brand: params?.brand,
              page: params?.page,
              page_size: params?.page_size,
              type: params?.type,
              q: params?.q,
              isBestOffer: params?.isBestOffer,
              isHit: params?.isHit,
              hasDiscount: params?.hasDiscount,
              hasGift: params?.hasGift,
              hasPromotion: params?.hasPromotion,
            },
          })
          .then((r) => r.data),

  getShowcases: () =>
    apiClient
      .get<ProductShowcaseListItemRaw[] | PaginatedResponse<ProductShowcaseListItemRaw>>(
        `${CATALOG_PREFIX}/showcases/`
      )
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : r.data.results
        return rows.map(normalizeShowcaseListItem)
      }),

  getShowcase: (slug: string, params?: { page?: number; page_size?: number }) =>
    apiClient
      .get<ProductShowcaseDetailRaw>(`${CATALOG_PREFIX}/showcases/${slug}/`, { params })
      .then((r) => normalizeShowcaseDetail(r.data)),

  getArrivals: (params?: { page?: number; page_size?: number }) =>
    apiClient
      .get<ArrivalsPage>(`${CATALOG_PREFIX}/arrivals/`, { params })
      .then((r) => r.data),

  getArrival: (slug: string, params?: { page?: number; page_size?: number }) =>
    apiClient
      .get<Omit<ArrivalDetail, 'products'> & { products: HomeProductRaw[] }>(
        `${CATALOG_PREFIX}/arrivals/${slug}/`,
        { params },
      )
      .then((r) => ({
        ...r.data,
        products: Array.isArray(r.data.products)
          ? r.data.products.map(normalizeHomeProduct)
          : [],
      })),

  getSubscriptions: (params?: { page?: number; page_size?: number }) =>
    apiClient
      .get<SubscriptionsPage>(`${CATALOG_PREFIX}/subscriptions/`, { params })
      .then((r) => r.data),
  subscribeVariant: (variantId: number, personalDataConsent = false) =>
    apiClient
      .post<SubscriptionItem>(
        `${CATALOG_PREFIX}/subscriptions/subscribe/${variantId}/`,
        { personal_data_consent: personalDataConsent },
      )
      .then((r) => r.data),
  unsubscribeVariant: (variantId: number) =>
    apiClient
      .delete(`${CATALOG_PREFIX}/subscriptions/unsubscribe/${variantId}/`)
      .then((r) => r.data),
  trackProduct: (slugOrId: string, payload: ProductTrackingPayload) =>
    apiClient
      .post<{ id: number; share_token: string; source_key: string; source_name: string }>(
        `${CATALOG_PREFIX}/products/${slugOrId}/track/`,
        payload
      )
      .then((r) => r.data),
}

const REVIEWS_PREFIX = '/api/public/reviews'

export const reviewsApi = {
  listProductReviews: (productId: number, params?: { page?: number; page_size?: number }) =>
    apiClient
      .get<PaginatedResponse<Review>>(`${REVIEWS_PREFIX}/products/${productId}/reviews/`, {
        params: { page: params?.page, page_size: params?.page_size },
      })
      .then((r) => r.data),
  createReview: (payload: CreateReviewRequest) =>
    apiClient.post<Review>(`${REVIEWS_PREFIX}/reviews/create/`, payload).then((r) => r.data),
  deleteReview: (reviewId: number) =>
    apiClient.delete(`${REVIEWS_PREFIX}/reviews/${reviewId}/delete/`).then((r) => r.data),
  updateReview: (reviewId: number, payload: { rating: number; text: string }) =>
    apiClient
      .patch<Review>(`${REVIEWS_PREFIX}/reviews/${reviewId}/update/`, payload)
      .then((r) => r.data),
  createReply: (payload: { review_id: number; text: string }) =>
    apiClient.post<ReviewReply>(`${REVIEWS_PREFIX}/reviews/reply/`, payload).then((r) => r.data),
  updateReply: (replyId: number, payload: { text: string }) =>
    apiClient
      .patch<ReviewReply>(`${REVIEWS_PREFIX}/reviews/reply/${replyId}/update/`, payload)
      .then((r) => r.data),
  deleteReply: (replyId: number) =>
    apiClient.delete(`${REVIEWS_PREFIX}/reviews/reply/${replyId}/delete/`).then((r) => r.data),
}

export type SubscriptionItem = {
  id: number
  variant_id: number
  product_name: string
  product_slug: string
  variant_sku: string
  is_active: boolean
  created_at: string
}
export type SubscriptionsPage = {
  count: number
  next: string | null
  previous: string | null
  results: SubscriptionItem[]
}

const ORDERS_PREFIX = '/api/internal/orders/orders'

export type OrderItem = {
  id: number
  fulfillment_id: number
  warehouse_id: number
  warehouse_name: string
  variant_id: number
  product_name: string
  sku: string
  quantity: number
  base_price: string
  base_total: string
  unit_price: string
  discount_amount: string
  discount_percent: number
  applied_promotion_code: string | null
  total_price: string
}

export type Order = {
  id: number
  status: string
  currency: string
  total_amount: string
  total_discount: string
  promocode: string | null
  contact_phone: string
  delivery_address_snapshot: string
  customer_full_name: string
  customer_email: string
  customer_comment: string
  receipt_uid: string
  has_receipt: boolean
  items: OrderItem[]
  created_at: string
  updated_at: string
}

export type OrderListResponse = {
  count: number
  next: string | null
  previous: string | null
  results: Order[]
}

export const ordersApi = {
  getOrders: (params?: { page?: number; page_size?: number }) =>
    apiClient
      .get<OrderListResponse>(ORDERS_PREFIX + '/', { params })
      .then((r) => r.data),
  getOrder: (orderId: number | string) =>
    apiClient.get<Order>(`${ORDERS_PREFIX}/${orderId}/`).then((r) => r.data),
  downloadReceipt: (orderId: number | string) =>
    apiClient
      .get<Blob>(`${ORDERS_PREFIX}/${orderId}/receipt/`, { responseType: 'blob' })
      .then((r) => r.data),
}

export default homeApi
